import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import type { InvoiceGroup } from '../../../../../../api/useFreight/types'
import dayjs from 'dayjs'
import React from 'react'
import { timeDisplay } from '../../../../shared'
import { formatters } from '@/utils/intl/dateTimeFormatters'

export type InvoiceStatus =
  | 'sent'
  | 'cr_approval'
  | 'blocked'
  | 'ready'
  | 'needs_attention'
  | 'open'
  | 'failure'
  | 'hold'

export const invoiceStatusConfig = {
  sent: {
    title: 'Sent',
    bgColor: 'success',
    iconColor: 'var(--theme-success)',
    icon: 'lock',
    renderInfo: (invoiceGroup: InvoiceGroup) => (
      <Vertical gap={4}>
        <Horizontal verticalCenter gap={4}>
          <Texto>Sent at</Texto>
          <Texto>{formatters.DATE_TIME(invoiceGroup.invoices?.[0]?.sent_date)}</Texto>
        </Horizontal>
      </Vertical>
    ),
  },
  cr_approval: {
    title: 'Credit/Rebill Approval',
    bgColor: 'warning',
    iconColor: 'var(--theme-warning)',
    icon: 'credit_score',
    renderInfo: (invoiceGroup: InvoiceGroup) => (
      <Horizontal verticalCenter gap={4}>
        <Texto appearance='light'>
          Awaiting approval since {timeDisplay(invoiceGroup.invoices?.[0]?.when_to_send_date).display}
        </Texto>
      </Horizontal>
    ),
  },
  blocked: {
    title: 'Blocked',
    bgColor: 'error',
    iconColor: 'var(--theme-error)',
    icon: 'error',
    renderInfo: (invoiceGroup: InvoiceGroup) => (
      <Horizontal verticalCenter gap={4}>
        <Texto appearance='error' weight='bold' style={{ color: 'var(--theme-error)' }}>
          Invoice date was{' '}
          {timeDisplay((invoiceGroup.invoices?.[0]?.when_to_send_date || new Date()).toString()).display}.
        </Texto>
      </Horizontal>
    ),
  },
  failure: {
    title: 'Failure',
    bgColor: 'error',
    iconColor: 'var(--theme-error)',
    icon: 'cancel',
    renderInfo: (invoiceGroup: InvoiceGroup) => (
      <Horizontal verticalCenter gap={4}>
        <Texto appearance='error' weight='bold' style={{ color: 'var(--theme-error)' }}>
          There was an issue processing this invoice.
        </Texto>
      </Horizontal>
    ),
  },
  ready: {
    title: 'Ready',
    bgColor: 'primary',
    iconColor: 'var(--theme-color-2)',
    icon: 'check_circle',
    renderInfo: (invoiceGroup: InvoiceGroup) => (
      <Horizontal verticalCenter gap={4}>
        <Texto>
          Sends @ {dayjs(invoiceGroup.invoices?.[0]?.when_to_send_date || new Date()).format('MM/DD/YYYY hh:mm A')}
        </Texto>
      </Horizontal>
    ),
  },
  needs_attention: {
    title: 'Needs Attention',
    bgColor: 'warning',
    iconColor: 'var(--theme-warning)',
    icon: 'warning',
    renderInfo: (invoiceGroup: InvoiceGroup) => (
      <Horizontal verticalCenter gap={4}>
        <Texto appearance='light'>
          This invoice has{' '}
          {
            invoiceGroup.invoices?.[0]?.transactions?.flatMap((transaction) =>
              transaction.details.filter((detail) => detail.status === 'needs_approval' || detail.status === 'error')
            ).length
          }
          {invoiceGroup.invoices?.[0]?.transactions?.flatMap((transaction) =>
            transaction.details.filter((detail) => detail.status === 'needs_approval' || detail.status === 'error')
          ).length === 1
            ? ' item '
            : ' items '}{' '}
          for review.
        </Texto>
      </Horizontal>
    ),
  },
  open: {
    title: 'Open',
    bgColor: 'default',
    iconColor: 'var(--text-primary)',
    icon: 'folder_open',
    renderInfo: (invoiceGroup: InvoiceGroup) => (
      <Horizontal verticalCenter gap={4}>
        <Texto appearance='light'>
          The invoice will send in{' '}
          {timeDisplay((invoiceGroup.invoices?.[0]?.when_to_send_date || new Date()).toString()).display}.
        </Texto>
      </Horizontal>
    ),
  },
  hold: {
    title: 'On Hold',
    bgColor: 'warning',
    iconColor: 'var(--theme-warning)',
    icon: 'pause_circle',
    renderInfo: (invoiceGroup: InvoiceGroup) => (
      <Horizontal verticalCenter gap={4}>
        <Texto appearance='light'>This invoice is currently on hold.</Texto>
      </Horizontal>
    ),
  },
} as const
